import React, {useEffect, useMemo, useState} from 'react'
import {
  PractitionerPatientEditDisabledReasonCode,
  PractitionerPatientIdentityValues,
} from '@common/models'
import {Sex} from '@common/models/medical'
import {useTranslation} from 'react-i18next'

import {SideModal} from '~/components/general/modal/SideModal'
import {Container} from '~/components/general/container/Container'
import {Typography} from '~/components/general/typography/Typography'
import {Input} from '~/components/general/input/Input'
import {Button} from '~/components/general/button/Button'
import {Option, Select} from '~/components/general/select/Select'
import {
  validatePhoneNumberInput,
  translatePhoneValidationError,
} from '~/utils/phoneValidation'

type Props = {
  isVisible: boolean
  isSaving: boolean
  canEdit: boolean
  reasonCodes?: PractitionerPatientEditDisabledReasonCode[]
  initialValues?: PractitionerPatientIdentityValues
  onClose: () => void
  onSave: (payload: PractitionerPatientIdentityValues) => void
}

export const EditPatientIdentityModal: React.FC<Props> = ({
  isVisible,
  onClose,
  initialValues,
  onSave,
  isSaving,
  canEdit,
  reasonCodes = [],
}) => {
  const {t} = useTranslation<string>()
  const [form, setForm] = useState<PractitionerPatientIdentityValues>({
    dateOfBirth: null,
    gender: null,
    phoneNumber: null,
  })
  const selectedGenderOption = useMemo(() => {
    if (!form.gender) return undefined
    const label = form.gender === Sex.MALE ? t('Male') : t('Female')
    return <Typography weight="medium">{label}</Typography>
  }, [form.gender, t])
  const translatedReasonCodeMap = useMemo(
    (): Record<PractitionerPatientEditDisabledReasonCode, string> => ({
      [PractitionerPatientEditDisabledReasonCode.NOT_IN_OWNED_CAMPAIGN]: t(
        'The patient is not a member of your facility. Please ask them if they have removed their access.',
      ),
      [PractitionerPatientEditDisabledReasonCode.PROTECTED_DEMOGRAPHICS]: t(
        'The patient has updated their own demographics so they cannot be edited here.',
      ),
    }),
    [t],
  )

  const translatedReasonCodes = useMemo(
    () => reasonCodes.map((reason) => translatedReasonCodeMap[reason]),
    [reasonCodes, translatedReasonCodeMap],
  )
  const initialDateOfBirth = initialValues?.dateOfBirth ?? null
  const initialGender = initialValues?.gender ?? null

  useEffect(() => {
    setForm({
      dateOfBirth: initialDateOfBirth,
      gender: initialGender,
      phoneNumber: initialValues?.phoneNumber ?? null,
    })
  }, [initialDateOfBirth, initialGender, initialValues?.phoneNumber])

  const phoneValidation = validatePhoneNumberInput(form.phoneNumber)

  return (
    <SideModal
      isVisible={isVisible}
      onClose={onClose}
      header={t('Edit patient details')}>
      <Container direction="column" spacing={2}>
        <Typography size="sm">{t('Date of birth')}</Typography>
        <Input
          type="date"
          value={form.dateOfBirth || ''}
          disabled={!canEdit}
          onChange={(e) =>
            setForm((prev) => ({...prev, dateOfBirth: e.target.value || null}))
          }
        />

        <Typography size="sm">{t('Gender')}</Typography>
        <Select
          placeholder={t('Select')}
          value={form.gender || ''}
          selectedOption={selectedGenderOption}
          size="small"
          disabled={!canEdit}>
          <Option
            onClick={() =>
              setForm((prev) => ({
                ...prev,
                gender: null,
              }))
            }>
            <Typography weight="medium">{t('Unspecified')}</Typography>
          </Option>
          <Option
            onClick={() =>
              setForm((prev) => ({
                ...prev,
                gender: Sex.MALE,
              }))
            }>
            <Typography weight="medium">{t('Male')}</Typography>
          </Option>
          <Option
            onClick={() =>
              setForm((prev) => ({
                ...prev,
                gender: Sex.FEMALE,
              }))
            }>
            <Typography weight="medium">{t('Female')}</Typography>
          </Option>
        </Select>

        <Typography size="sm">{t('Phone number')}</Typography>
        <Input
          type="tel"
          value={form.phoneNumber || ''}
          disabled={!canEdit}
          errorMessage={
            phoneValidation.error
              ? translatePhoneValidationError(phoneValidation.error.code, t)
              : undefined
          }
          onChange={(e) =>
            setForm((prev) => ({...prev, phoneNumber: e.target.value || null}))
          }
        />
        {!canEdit && (
          <Container direction="column" spacing={1}>
            <Typography size="sm" color="red" weight="medium">
              {t('Editing is currently unavailable for this patient.')}
            </Typography>
            {translatedReasonCodes.length > 0 && (
              <Typography size="sm" color="red">
                {t('Reasons')}: {translatedReasonCodes.join(', ')}
              </Typography>
            )}
          </Container>
        )}

        {canEdit && (
          <Container flex justify="flex-end" spacing={1}>
            <Button color="primary" onClick={onClose}>
              {t('Cancel')}
            </Button>
            <Button
              onClick={() => onSave(form)}
              disabled={!phoneValidation.isValid || isSaving}>
              {t('Save')}
            </Button>
          </Container>
        )}
      </Container>
    </SideModal>
  )
}
